import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { setupCache, IAxiosCacheAdapterOptions } from "axios-cache-adapter";
import { Observable, Subject } from "rxjs";
import env from "../environment/env.json";
import store from "@/store";

const STORAGE_KEY_VALID_UNTIL = "token-valid-until";
const STORAGE_KEY_ACCESS_TOKEN = "access-token";
const STORAGE_KEY_ACCESS_TOKEN_TYPE = "access-token-type";
const STORAGE_KEY_REFRESH_TOKEN = "refresh-token";
const STORAGE_KEY_ID_TOKEN = "id-token";
const STORAGE_KEY_NAME = "user-name";

function _unwrapAxiosResponse<T>(
  responsePromise: Promise<AxiosResponse<T>>,
  givenResultSubject?: Subject<T>
): Observable<T> {
  const resultSubject = givenResultSubject || new Subject<T>();
  responsePromise
    .then(response => {
      resultSubject.next(response.data);
      resultSubject.complete();
    })
    .catch(error => {
      resultSubject.error(error);
    });
  return resultSubject;
}

/** Based on a copy of the function in axios-cache-adapter/utilities.js */
function mapObject(prefix: string, fn: (elem: any, key: string) => any) {
  prefix = prefix + "/";
  return Object.keys(localStorage)
    .filter(globalKey => globalKey.startsWith(prefix))
    .map(key => fn(localStorage.getItem(key), key.substr(prefix.length)));
}

/** An Axios Cache Store implementation based on localStorage */
class AxiosLocalStore {
  constructor(private prefix: string) {}
  async getItem(key: string) {
    const item = localStorage.getItem(`${this.prefix}/${key}`);
    return item ? JSON.parse(item) : null;
  }

  async setItem(key: string, value: any) {
    localStorage.setItem(`${this.prefix}/${key}`, JSON.stringify(value));
    return value;
  }

  async removeItem(key: string) {
    localStorage.removeItem(`${this.prefix}/${key}`);
  }

  async clear() {
    const prefix = this.prefix + "/";
    Object.keys(localStorage)
      .filter(globalKey => globalKey.startsWith(prefix))
      .forEach(key => localStorage.removeItem(key));
  }

  async length() {
    const prefix = this.prefix + "/";
    return Object.keys(localStorage).filter(globalKey =>
      globalKey.startsWith(prefix)
    ).length;
  }

  iterate(fn: (elem: any, key: string) => any) {
    return Promise.all(mapObject(this.prefix, fn));
  }
}

const makeHttpClient = (config?: AxiosRequestConfig) => {
  config = Object.assign(
    { headers: { "Content-Type": "application/json; charset=utf8" } },
    config
  );
  const httpClient = axios.create(config);
  httpClient.interceptors.request.use(config => {
    if (!config.url?.startsWith("http")) {
      config.url = (env.scoreApiUrl + config.url).replace(
        new RegExp("([^:]/)/"),
        "\\1"
      );
    }

    if ("Authorization" in axios.defaults.headers.common) {
      config.headers.Authorization =
        axios.defaults.headers.common["Authorization"];
    }

    return config;
  });

  // Response interceptor to handle errors globally
  httpClient.interceptors.response.use(
    response => {
      // Check if response is HTML instead of expected JSON
      if (
        typeof response.data === "string" &&
        response.data.trim().startsWith("<!DOCTYPE")
      ) {
        store.commit("setBackendDown", {
          isDown: true,
          message:
            "The server returned an unexpected response. Please try again later."
        });
        // Reject this response so it gets caught as an error
        return Promise.reject({
          isHtmlError: true,
          message: "Server returned HTML instead of JSON",
          response: response
        });
      }

      // Clear any previous error states on successful response
      store.commit("clearError");
      return response;
    },
    error => {
      // Handle different types of errors
      if (!error.response) {
        // Network error - backend server is down
        console.error("Network error: Backend server is unreachable", error);
        store.commit("setBackendDown", {
          isDown: true,
          message:
            "Unable to connect to the server. Please check your internet connection or try again later."
        });
      } else if (error.response.status >= 500) {
        // Server error (5xx)
        console.error(
          "Server error:",
          error.response.status,
          error.response.data
        );
        store.commit("setBackendDown", {
          isDown: true,
          message: `Server error (${error.response.status}). Our team has been notified. Please try again later.`
        });
      } else if (
        error.response.status === 401 ||
        error.response.status === 403
      ) {
        // Authentication/Authorization errors
        console.error("Authentication error:", error.response.status);
        // Don't set error state here as Keycloak will handle this
        // But we can log it for debugging
      }

      return Promise.reject(error);
    }
  );

  return httpClient;
};

const cachingHttpClient = (
  cachePrefix: string,
  ttlInSeconds: number,
  config?: AxiosRequestConfig & IAxiosCacheAdapterOptions
) =>
  makeHttpClient(
    Object.assign(
      {
        adapter: setupCache({
          maxAge: ttlInSeconds * 1000,
          store: new AxiosLocalStore(cachePrefix),
          debug: !env.isProduction
        }).adapter
      },
      config
    )
  );

export {
  cachingHttpClient,
  makeHttpClient,
  _unwrapAxiosResponse,
  STORAGE_KEY_VALID_UNTIL,
  STORAGE_KEY_ACCESS_TOKEN,
  STORAGE_KEY_ACCESS_TOKEN_TYPE,
  STORAGE_KEY_REFRESH_TOKEN,
  STORAGE_KEY_ID_TOKEN,
  STORAGE_KEY_NAME
};
